import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { PageHeader } from '@/components/core';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { useToast } from '@/hooks/use-toast';
import { copyTextToClipboard } from '@/lib/clipboard';
import { mergeWhere } from '@/lib/table-query';
import { useAuthStore } from '@/state/store/auth';
import { useDepartmentScope } from '@/modules/shared/hr-lookups';
import { createEmployeeTableColumns } from '../../components/employee-table-columns/employee-table-columns';
import { FineDialog } from '../../components/fine-dialog/fine-dialog';
import { RoleAssignDialog } from '../../components/role-assign-dialog/role-assign-dialog';
import { WfoDaysDialog } from '../../components/work-status/wfo-days-dialog';
import {
  useEmployeeCurrentWorkDays,
  useEmployeesLoginActive,
  useGetEmployees,
  useSetEmployeeActive,
  useUpdateEmployeeWorkStatus,
} from '../../hooks/use-employees';
import { DEFAULT_EMPLOYEES_ORDER } from '../../graphql/queries';
import { workFromHomeOff, workFromHomeOn, WorkStatusChanges } from '../../schemas/work-status';
import { Employee, EmployeesPagingResult } from '../../types/employee.types';
import { useReportUnreportedError } from '@/state/query-client/hooks';

export const EmployeeListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const canCreate = useHasPermission(HRM_PERMISSIONS.DIRECTORY.CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.DIRECTORY.UPDATE);
  const currentUserId = useAuthStore((state) => state.user?.itemId);
  const canChangeWfh = useHasPermission(HRM_PERMISSIONS.DIRECTORY.CHANGE_WFH);
  const canCreateFine = useHasPermission(HRM_PERMISSIONS.DIRECTORY.CREATE_FINE);
  const canAssignRole = useHasPermission(HRM_PERMISSIONS.DIRECTORY.ASSIGN_ROLE);
  // Without DEPARTMENT.VIEW_ALL_DEPARTMENTS, the directory is limited to the departments whose Head
  // of Department role the person holds (legacy "HOB" behavior: a department head only ever saw their
  // own department's employees).
  const { departments, getDepartmentIdCondition } = useDepartmentScope();
  const [statusTarget, setStatusTarget] = useState<Employee | null>(null);
  // The header spot the cards' filter button is rendered into on a phone.
  const [filterSlot, setFilterSlot] = useState<HTMLSpanElement | null>(null);
  const [officeDaysTarget, setOfficeDaysTarget] = useState<Employee | null>(null);
  const { data: officeTargetWorkDays, isLoading: isLoadingOfficeTargetWorkDays } =
    useEmployeeCurrentWorkDays(officeDaysTarget?.UserId);
  const [fineTarget, setFineTarget] = useState<Employee | null>(null);
  const [roleTarget, setRoleTarget] = useState<Employee | null>(null);
  const {
    mutateAsync: updateWorkStatus,
    isPending: isSavingWorkStatus,
    variables: workStatusVariables,
  } = useUpdateEmployeeWorkStatus();
  const { mutateAsync: setEmployeeActive, isPending: isChangingStatus } = useSetEmployeeActive();
  const reportError = useReportUnreportedError();

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_EMPLOYEES_ORDER,
  });

  // The name, ID and email are filtered from their columns; this keeps the department scope.
  const departmentCondition = getDepartmentIdCondition('');
  const filter = departmentCondition ? { DepartmentId: departmentCondition } : {};

  const { data, isLoading, error } = useGetEmployees({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { Employees?: EmployeesPagingResult })?.Employees;
  const items = useEmployeesLoginActive(result?.items);
  const totalCount = result?.totalCount ?? 0;

  /** Saves the change; a failure is reported and keeps the dialog open. */
  const saveWorkStatus = async (employee: Employee, changes: WorkStatusChanges) => {
    try {
      await updateWorkStatus({ employee, changes });
      return true;
    } catch (error) {
      reportError(error);
      return false;
    }
  };

  const handleWfhChange = (employee: Employee, next: boolean) => {
    if (next) {
      void saveWorkStatus(employee, workFromHomeOn(employee));
      return;
    }
    // Everyone picks their office days before work from home turns off.
    setOfficeDaysTarget(employee);
  };

  const handleOfficeDaysConfirm = async (days: string[]) => {
    if (!officeDaysTarget) return;
    const changes = workFromHomeOff(days);
    if (changes && (await saveWorkStatus(officeDaysTarget, changes))) setOfficeDaysTarget(null);
  };

  const handleCopyEmail = async (employee: Employee) => {
    const copied = employee.Email ? await copyTextToClipboard(employee.Email) : false;
    toast(
      copied ? { title: t('EMAIL_COPIED') } : { variant: 'destructive', title: t('COPY_FAILED') }
    );
  };

  const handleCopyEmployeeId = async (employee: Employee) => {
    const copied = employee.EmployeeID ? await copyTextToClipboard(employee.EmployeeID) : false;
    toast(
      copied
        ? { title: t('EMPLOYEE_ID_COPIED') }
        : { variant: 'destructive', title: t('COPY_FAILED') }
    );
  };

  const columns = createEmployeeTableColumns({
    t,
    onToggleActive: canUpdate ? setStatusTarget : undefined,
    currentUserId,
    onCopyEmail: handleCopyEmail,
    onCopyEmployeeId: handleCopyEmployeeId,
    onFine: canCreateFine ? setFineTarget : undefined,
    onAssignRole: canAssignRole ? setRoleTarget : undefined,
    departments,
    workStatus: canChangeWfh
      ? {
          savingEmployeeId: isSavingWorkStatus ? workStatusVariables?.employee.ItemId : undefined,
          onWfhChange: handleWfhChange,
          onOfficeDaysEdit: setOfficeDaysTarget,
        }
      : undefined,
  });

  const isReactivating = statusTarget?.IsActive === false;
  const handleStatusConfirm = async () => {
    if (!statusTarget) return;
    try {
      await setEmployeeActive({ employee: statusTarget, isActive: isReactivating });
      setStatusTarget(null);
    } catch (error) {
      // The dialog stays open so it can be retried.
      reportError(error);
    }
  };

  const handleRowClick = (employee: Employee) => navigate(`/hr/directory/${employee.ItemId}`);

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        // On a phone the cards' filter button sits here, left of Add Employee.
        beforeActions={<span ref={setFilterSlot} className="contents md:hidden" />}
        title={t('EMPLOYEE_DIRECTORY')}
        actions={
          canCreate
            ? [
                {
                  label: t('ADD_EMPLOYEE'),
                  icon: <Plus className="size-5" />,
                  onClick: () => navigate('/hr/directory/new/create'),
                },
              ]
            : []
        }
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
        mobileFilterSlot={filterSlot}
      />

      <WfoDaysDialog
        open={officeDaysTarget !== null}
        onOpenChange={(open) => !open && setOfficeDaysTarget(null)}
        initialDays={officeDaysTarget?.WFODays}
        workDays={officeTargetWorkDays}
        isLoadingWorkDays={isLoadingOfficeTargetWorkDays}
        isSaving={isSavingWorkStatus}
        onConfirm={handleOfficeDaysConfirm}
      />

      <FineDialog
        open={fineTarget !== null}
        onOpenChange={(open) => !open && setFineTarget(null)}
        employee={fineTarget}
      />

      <RoleAssignDialog
        open={roleTarget !== null}
        onOpenChange={(open) => !open && setRoleTarget(null)}
        employee={roleTarget}
      />

      <ConfirmationModal
        open={statusTarget !== null}
        onOpenChange={(open) => !open && setStatusTarget(null)}
        title={isReactivating ? t('REACTIVATE_USER') : t('DEACTIVATE_USER')}
        description={
          isReactivating
            ? t('ARE_YOU_SURE_TO_REACTIVATE_USER')
            : t('ARE_YOU_SURE_TO_DEACTIVATE_USER')
        }
        confirmText={isReactivating ? t('REACTIVATE') : t('DEACTIVATE')}
        preventAutoClose={isChangingStatus}
        onConfirm={handleStatusConfirm}
      />
    </div>
  );
};
